/**
 * useTabSwipe — index-based swipe-to-change-tab gesture.
 *
 * Thin wrapper around the shared `useSwipeGesture` touch mechanics (see
 * ./use-swipe-gesture.ts for axis-lock/threshold/fire-on-release details).
 * This layer only adds the next/prev index resolution:
 *  - swipe left  → next = min(activeIndex + 1, count - 1)
 *  - swipe right → prev = max(activeIndex - 1, 0)
 * With `loop: true` the edges wrap instead of clamping, matching the endless
 * top SectionSwitcher ribbon (last → first and first → last).
 * Fires the same `haptic.light()` tap used by the SectionSwitcher ribbon
 * whenever a swipe actually commits to a new index — never on a clamped
 * edge swipe or a below-threshold drag that resolves back to the same tab.
 *
 * Used by the Explore page-body content swipe (HomePage.tsx) and the Profile
 * filter-pill swipe.
 *
 * Usage:
 *   const { swipeCallbackRef } = useTabSwipe({ count: TABS.length, activeIndex, onIndexChange });
 *   <div ref={swipeCallbackRef} style={{ overflow: 'hidden', touchAction: 'pan-y' }}>
 *     ...content...
 *   </div>
 */

import { useRef, useCallback, useEffect } from 'react';
import { useSwipeGesture } from './use-swipe-gesture';
import { haptic } from '@/utils/haptic';

interface UseTabSwipeOptions {
  /** Total number of tabs / filter keys */
  count: number;
  /** Currently active index */
  activeIndex: number;
  /** Called with the new index when a swipe is detected */
  onIndexChange: (index: number) => void;
  /** Minimum horizontal distance in pixels to trigger a tab change (default: 50) */
  threshold?: number;
  /** When false the gesture is inert — no axis lock, no preventDefault, no index change */
  enabled?: boolean;
  /** When true the edges wrap around (last → first, first → last) instead of clamping */
  loop?: boolean;
}

export function useTabSwipe({
  count,
  activeIndex,
  onIndexChange,
  threshold = 50,
  enabled = true,
  loop = false,
}: UseTabSwipeOptions) {
  // Mirrors of the latest props so the stable swipe callbacks below always
  // read current values without needing to be recreated per render.
  const countRef = useRef(count);
  useEffect(() => {
    countRef.current = count;
  }, [count]);

  const activeIndexRef = useRef(activeIndex);
  useEffect(() => {
    activeIndexRef.current = activeIndex;
  }, [activeIndex]);

  const onIndexChangeRef = useRef(onIndexChange);
  useEffect(() => {
    onIndexChangeRef.current = onIndexChange;
  }, [onIndexChange]);

  const loopRef = useRef(loop);
  useEffect(() => {
    loopRef.current = loop;
  }, [loop]);

  const onSwipeLeft = useCallback(() => {
    const current = activeIndexRef.current;
    const count = countRef.current;
    const next = loopRef.current
      ? (current + 1) % count
      : Math.min(current + 1, count - 1);
    if (next !== current) {
      haptic.light();
      onIndexChangeRef.current(next);
    }
  }, []);

  const onSwipeRight = useCallback(() => {
    const current = activeIndexRef.current;
    const count = countRef.current;
    const prev = loopRef.current
      ? (current - 1 + count) % count
      : Math.max(current - 1, 0);
    if (prev !== current) {
      haptic.light();
      onIndexChangeRef.current(prev);
    }
  }, []);

  return useSwipeGesture({ onSwipeLeft, onSwipeRight, threshold, enabled });
}
